import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { auth, db, storage, ADMIN_EMAIL } from '../../firebase/config';
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaImage,
  FaSave,
  FaTimes,
  FaUsers,
  FaStar,
  FaGlobe,
  FaQuoteLeft
} from 'react-icons/fa';
import './AdminClients.css';

const AdminClients = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientsList, setClientsList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [storyImagePreview, setStoryImagePreview] = useState(null);
  const [storyImageFile, setStoryImageFile] = useState(null);
  const [activeTab, setActiveTab] = useState('en');
  const [formData, setFormData] = useState({
    name: '',
    logoUrl: '',
    rating: 5,
    // English content
    successStory_en: '',
    storyAuthor_en: '',
    // French content
    successStory_fr: '',
    storyAuthor_fr: '',
    // Common
    storyImageUrl: '',
    order: 0
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        setUser(currentUser);
        fetchClients();
      } else {
        navigate('/admin');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchClients = async () => {
    try {
      const q = query(collection(db, 'clients'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClientsList(items);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error fetching clients:', err);
      // Check if it's a permissions error - likely Firestore rules not updated yet
      if (err.code === 'permission-denied' || err.message?.includes('permission')) {
        setError('Firestore permissions not configured. Please update Firestore rules in Firebase Console to include the "clients" collection. See FIREBASE_SETUP.md for details.');
      } else {
        setError('Failed to load clients');
      }
      setClientsList([]); // Set empty list so UI still works
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleStoryImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setStoryImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setStoryImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file, folder) => {
    try {
      const fileName = `${folder}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      return await getDownloadURL(snapshot.ref);
    } catch (err) {
      console.error('Upload error:', err);
      if (err.code === 'storage/unauthorized') {
        throw new Error('Permission denied. Please update Firebase Storage rules.');
      }
      throw err;
    }
  };

  const deleteImage = async (imageUrl) => {
    if (imageUrl && imageUrl.includes('firebase')) {
      try {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      } catch (err) {
        console.log('Image deletion error:', err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      logoUrl: '',
      rating: 5,
      successStory_en: '',
      storyAuthor_en: '',
      successStory_fr: '',
      storyAuthor_fr: '',
      storyImageUrl: '',
      order: clientsList.length
    });
    setLogoPreview(null);
    setLogoFile(null);
    setStoryImagePreview(null);
    setStoryImageFile(null);
    setEditingId(null);
    setShowForm(false);
    setActiveTab('en');
    setError('');
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name || '',
      logoUrl: item.logoUrl || '',
      rating: item.rating || 5,
      successStory_en: item.successStory_en || item.successStory || '',
      storyAuthor_en: item.storyAuthor_en || item.storyAuthor || '',
      successStory_fr: item.successStory_fr || '',
      storyAuthor_fr: item.storyAuthor_fr || '',
      storyImageUrl: item.storyImageUrl || '',
      order: item.order || 0
    });
    setLogoPreview(item.logoUrl || null);
    setStoryImagePreview(item.storyImageUrl || null);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Client name is required.');
      return;
    }

    setSaving(true);

    try {
      let logoUrl = formData.logoUrl;
      let storyImageUrl = formData.storyImageUrl;

      if (logoFile) {
        logoUrl = await uploadImage(logoFile, 'clients/logos');
      }

      if (storyImageFile) {
        storyImageUrl = await uploadImage(storyImageFile, 'clients/stories');
      }

      const clientData = {
        name: formData.name.trim(),
        logoUrl,
        rating: formData.rating,
        // English
        successStory_en: formData.successStory_en.trim(),
        storyAuthor_en: formData.storyAuthor_en.trim(),
        // French
        successStory_fr: formData.successStory_fr.trim(),
        storyAuthor_fr: formData.storyAuthor_fr.trim(),
        // Legacy fields
        successStory: formData.successStory_en.trim(),
        storyAuthor: formData.storyAuthor_en.trim(),
        // Common
        storyImageUrl,
        order: formData.order,
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'clients', editingId), clientData);
        setSuccess('Client updated successfully!');
      } else {
        clientData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'clients'), clientData);
        setSuccess('Client added successfully!');
      }

      await fetchClients();
      resetForm();
    } catch (err) {
      console.error('Error saving client:', err);
      setError(err.message || 'Failed to save client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      return;
    }

    try {
      if (item.logoUrl) await deleteImage(item.logoUrl);
      if (item.storyImageUrl) await deleteImage(item.storyImageUrl);
      await deleteDoc(doc(db, 'clients', item.id));
      setSuccess('Client deleted successfully!');
      await fetchClients();
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Failed to delete client');
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <FaStar
        key={index}
        className={`star-icon ${index < rating ? 'filled' : ''}`}
        onClick={() => setFormData({ ...formData, rating: index + 1 })}
      />
    ));
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FaSpinner className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-clients">
      <header className="admin-clients-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <FaArrowLeft /> Back to Dashboard
        </button>
        <h1><FaUsers /> Reference Clients</h1>
      </header>

      <main className="admin-clients-content">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {!showForm ? (
          <>
            <div className="clients-actions">
              <button onClick={() => setShowForm(true)} className="add-btn">
                <FaPlus /> Add New Client
              </button>
            </div>

            <div className="clients-list">
              {clientsList.length === 0 ? (
                <div className="empty-state">
                  <FaUsers />
                  <h3>No clients yet</h3>
                  <p>Click "Add New Client" to create your first reference client.</p>
                </div>
              ) : (
                clientsList.map((item, index) => (
                  <div key={item.id} className="client-item">
                    <div className="client-item-order">
                      <span>{index + 1}</span>
                    </div>
                    <div className="client-item-logo">
                      {item.logoUrl ? (
                        <img src={item.logoUrl} alt={item.name} />
                      ) : (
                        <div className="no-image"><FaImage /></div>
                      )}
                    </div>
                    <div className="client-item-content">
                      <h3>{item.name}</h3>
                      <div className="client-item-rating">
                        {Array.from({ length: item.rating || 5 }, (_, i) => (
                          <FaStar key={i} className="star-filled" />
                        ))}
                      </div>
                      {item.successStory_en && (
                        <p className="client-item-story">
                          <FaQuoteLeft /> {item.successStory_en.substring(0, 100)}...
                        </p>
                      )}
                    </div>
                    <div className="client-item-actions">
                      <button onClick={() => handleEdit(item)} className="edit-btn">
                        <FaEdit /> Edit
                      </button>
                      <button onClick={() => handleDelete(item)} className="delete-btn">
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="client-form-container">
            <h2>{editingId ? 'Edit Client' : 'Add New Client'}</h2>

            <form onSubmit={handleSubmit} className="client-form">
              {/* Basic Info */}
              <div className="form-section">
                <h3>Basic Information</h3>

                <div className="form-group">
                  <label>Client Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., LVMH"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Client Logo</label>
                  <div className="image-upload-area">
                    {logoPreview ? (
                      <div className="image-preview logo-preview">
                        <img src={logoPreview} alt="Logo Preview" />
                        <button
                          type="button"
                          onClick={() => {
                            setLogoPreview(null);
                            setLogoFile(null);
                            setFormData({ ...formData, logoUrl: '' });
                          }}
                          className="remove-image-btn"
                        >
                          <FaTimes /> Remove
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-label">
                        <FaImage />
                        <span>Click to upload logo</span>
                        <small>Recommended: PNG with transparent background</small>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          hidden
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Rating</label>
                  <div className="rating-selector">
                    {renderStars(formData.rating)}
                    <span className="rating-text">{formData.rating} / 5</span>
                  </div>
                </div>
              </div>

              {/* Success Story - Language Tabs */}
              <div className="form-section">
                <h3>Success Story</h3>

                <div className="language-tabs">
                  <button
                    type="button"
                    className={`lang-tab ${activeTab === 'en' ? 'active' : ''}`}
                    onClick={() => setActiveTab('en')}
                  >
                    <FaGlobe /> English
                  </button>
                  <button
                    type="button"
                    className={`lang-tab ${activeTab === 'fr' ? 'active' : ''}`}
                    onClick={() => setActiveTab('fr')}
                  >
                    <FaGlobe /> French
                  </button>
                </div>

                {/* English Content */}
                <div className={`lang-content ${activeTab === 'en' ? 'active' : ''}`}>
                  <div className="form-group">
                    <label>Success Story (English)</label>
                    <textarea
                      value={formData.successStory_en}
                      onChange={(e) => setFormData({ ...formData, successStory_en: e.target.value })}
                      placeholder="Share the client's success story..."
                      rows={4}
                    />
                  </div>

                  <div className="form-group">
                    <label>Author Name (English)</label>
                    <input
                      type="text"
                      value={formData.storyAuthor_en}
                      onChange={(e) => setFormData({ ...formData, storyAuthor_en: e.target.value })}
                      placeholder="e.g., John Doe, CTO at LVMH"
                    />
                  </div>
                </div>

                {/* French Content */}
                <div className={`lang-content ${activeTab === 'fr' ? 'active' : ''}`}>
                  <div className="form-group">
                    <label>Success Story (French)</label>
                    <textarea
                      value={formData.successStory_fr}
                      onChange={(e) => setFormData({ ...formData, successStory_fr: e.target.value })}
                      placeholder="Partagez l'histoire de succès du client..."
                      rows={4}
                    />
                  </div>

                  <div className="form-group">
                    <label>Author Name (French)</label>
                    <input
                      type="text"
                      value={formData.storyAuthor_fr}
                      onChange={(e) => setFormData({ ...formData, storyAuthor_fr: e.target.value })}
                      placeholder="e.g., Jean Dupont, Directeur Technique chez LVMH"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Success Story Image</label>
                  <div className="image-upload-area">
                    {storyImagePreview ? (
                      <div className="image-preview">
                        <img src={storyImagePreview} alt="Story Preview" />
                        <button
                          type="button"
                          onClick={() => {
                            setStoryImagePreview(null);
                            setStoryImageFile(null);
                            setFormData({ ...formData, storyImageUrl: '' });
                          }}
                          className="remove-image-btn"
                        >
                          <FaTimes /> Remove
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-label">
                        <FaImage />
                        <span>Click to upload story image</span>
                        <small>An image that represents the success story</small>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleStoryImageChange}
                          hidden
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Display Order */}
              <div className="form-section">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="cancel-btn">
                  <FaTimes /> Cancel
                </button>
                <button type="submit" disabled={saving} className="save-btn">
                  {saving ? (
                    <><FaSpinner className="spinner" /> Saving...</>
                  ) : (
                    <><FaSave /> {editingId ? 'Update' : 'Save'} Client</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminClients;
